import { Circle, Input, InputProps, SquareProps } from "@chakra-ui/react";
import { forwardRef } from "react";
import { useDebounceCallback } from "usehooks-ts";

// Create a color wheel background using CSS conic-gradient
const colorWheelBackground = `conic-gradient(
      from 0deg,
      #ff0000 0deg,
      #ffff00 60deg,
      #00ff00 120deg,
      #00ffff 180deg,
      #0000ff 240deg,
      #ff00ff 300deg,
      #ff0000 360deg
    )`;

export type ColorPickerProps = Omit<SquareProps, "onChange"> & {
  value?: string;
  isDisabled?: boolean;
  _input?: InputProps;
  onChange?: (color: string) => void;
};

/**
 * ColorPicker component with a circular design and color wheel background.
 * The input is hidden and the circle acts as the visual representation.
 */
export const ColorPicker = forwardRef<HTMLInputElement, ColorPickerProps>(
  (
    {
      "aria-label": ariaLabel = "Color picker",
      value = "#65687b",
      size = 5,
      isDisabled = false,
      _input,
      children,
      onChange,
      ...rest
    },
    ref,
  ) => {
    const debouncedOnChange = useDebounceCallback(onChange ?? (() => {}), 50);

    return (
      <Circle
        _hover={!isDisabled ? { transform: "scale(1.05)" } : {}}
        background={colorWheelBackground}
        cursor={isDisabled ? "not-allowed" : "pointer"}
        opacity={isDisabled ? 0.5 : 1}
        overflow="hidden"
        position="relative"
        size={size}
        transition="transform 0.2s"
        {...rest}
      >
        <Input
          ref={ref}
          aria-label={ariaLabel}
          cursor="pointer"
          disabled={isDisabled}
          height="100%"
          left="0"
          opacity="0"
          position="absolute"
          top="0"
          type="color"
          value={value}
          width="100%"
          onChange={(e) => debouncedOnChange(e.target.value)}
          {..._input}
        />
        {children}
      </Circle>
    );
  },
);

ColorPicker.displayName = "ColorPicker";
